import { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
    makeMemberAnAdmin, 
    makeMemberAModerator, 
    removeMember, 
    removeAModerator, 
    removeAnAdmin,
    resetMemberLoadingState
} from '../../../features/memberships/membershipSlice'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import styled from 'styled-components';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { setConfirmModalData, setViewConfirmModal } from 'features/modal/modalSlice';
import useToggleDropdown from 'hooks/useToggleDropdown';
import { useSelector } from 'react-redux';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { MemberNameWrap } from './memberships.styled';

import { Modal, Tooltip } from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import CloseIcon from '@mui/icons-material/Close';
import SpinnerSmall from 'components/SpinnerSmall';
import { toast } from 'react-toastify';
import membershipService from 'features/memberships/membershipService';
import { useTranslation } from 'react-i18next'

function MyMembersItem( { userId, fullName, image, email, index } ) {
  const { t: translate } = useTranslation()
//should be paginated since churches will potentially have a lot of members
//alos, allow to search your members
    const ref1 = useRef()
    const dispatch = useDispatch()
    const [isDropped, setIsDropped] = useToggleDropdown(ref1)
    const [isAdmin, setIsAdmin] = useState(false)
    const [isModerator, setIsModerator] = useState(false)
    const [rolesOpen, setRolesOpen] = useState(false)
    const [roles, setRoles] = useState([])
    const [selectedRoleIds, setSelectedRoleIds] = useState([])
    const [rolesLoading, setRolesLoading] = useState(false)
    const [rolesSaving, setRolesSaving] = useState(false)
    const { user } = useSelector((state) => state.auth)

    const { 
        isMemberActionSuccess, 
        removeFromChurchSuccess, 
        admins, 
        moderators 
    } = useSelector((state) => state.memberships2)
  
    const handleDropDown = () => { 
        setIsDropped.toggle(ref1) 
    }

    const loadMemberRoles = async () => {
        setRolesOpen(true)
        setRolesLoading(true)
        try {
            const [rolesData, membershipData] = await Promise.all([
                membershipService.getMinistryRoles(user._id, user.token),
                membershipService.getMemberMembershipDetails(user._id, userId, user.token)
            ])
            setRoles(rolesData.ministryRoles || [])
            setSelectedRoleIds((membershipData.membership?.ministryRoles || []).map((role) => String(role.id)))
        } catch (error) {
            toast.error(translate('settings:membership.rolesLoadFailed'))
        } finally {
            setRolesLoading(false)
        }
    }

    const toggleRole = (roleId) => {
        setSelectedRoleIds((current) => current.includes(roleId)
            ? current.filter((id) => id !== roleId)
            : [...current, roleId]
        )
    }

    const saveMemberRoles = async () => {
        setRolesSaving(true)
        try {
            const data = await membershipService.updateMemberMembershipDetails(user._id, userId, {
                ministryRoleIds: selectedRoleIds,
            }, user.token)
            setSelectedRoleIds((data.membership?.ministryRoles || []).map((role) => String(role.id)))
            toast.success(translate('settings:membership.rolesUpdated'))
            setRolesOpen(false)
        } catch (error) {
            toast.error(error?.response?.data?.message || translate('settings:roles.memberRemoveFailed'))
        } finally {
            setRolesSaving(false)
        }
    }
    
    useEffect(() => {        
        setIsAdmin(admins.includes(userId))
        setIsModerator(moderators.includes(userId))
    }, [admins, moderators, userId, isMemberActionSuccess])


    const handleRemove = () => {
        dispatch(setConfirmModalData( {
            text: `${translate("web:settings.remove")} ${fullName} ${translate("web:settings.fromMembership") }?`,
            handler: () => dispatch(removeMember( { userId: userId } )),
            confirmBtnBackgroundHover: 'red',
            
        } ))
        
        dispatch(setViewConfirmModal(true))
        // dispatch(removeMember( { userId: userId } ))
    }
      
    const handleRemoveModerator = () => {
        dispatch(setConfirmModalData( {
            text: `${translate("web:settings.remove")} ${fullName} ${translate("web:settings.removeModerator") }?`,
            handler: () => dispatch(removeAModerator( { userId: userId } )),
            confirmBtnBackgroundHover: 'red',
        } ))
        dispatch(setViewConfirmModal(true))
    }

    const handleRemoveAdmin = () => {
        dispatch(setConfirmModalData( {
            text: `${translate("web:settings.remove")} ${fullName} ${translate("web:settings.removeAdmin") }?`,
            handler: () => dispatch(removeAnAdmin( { userId: userId } )),
            confirmBtnBackgroundHover: 'red',
        } ))
        dispatch(setViewConfirmModal(true))
    }
  
    const handleMakeAdmin = () => {
        dispatch(setConfirmModalData( {
            text: `${translate("web:settings.make")} ${fullName} ${translate("web:settings.admin")}?`,
            handler: () => dispatch(makeMemberAnAdmin({ 
                userId: userId,
                index: Number(index)
            })),
            confirmBtnBackgroundHover: 'green',
        } ))
        
        dispatch(setViewConfirmModal(true))
    }     

    const handleMakeModerator = () => {
        dispatch(setConfirmModalData( {
            text: `${translate("web:settings.make")} ${fullName} ${translate("web:settings.moderator")}?`,
            handler: () => dispatch(makeMemberAModerator( { userId: userId } )),
            confirmBtnBackgroundHover: 'green',
        } ))

        dispatch(setViewConfirmModal(true))
    }

    useEffect(() => {
        if(removeFromChurchSuccess){
            dispatch(setConfirmModalData({}))
            dispatch(setViewConfirmModal(false))

            dispatch(resetMemberLoadingState({
                valuesToResetArray: ['removeFromChurchSuccess', 'removeFromChurchLoading']
            }))

        }
    }, [removeFromChurchSuccess, dispatch])
 


    return (
        <MemberRow>
                <MemberInfoBlock> 
                        <MemberAvatar src={image} alt='' />
 
                    
                    <MemberNameWrap>
                        <MemberText>
                            <MemberName>{fullName}</MemberName>
                            {email && <MemberEmail>{email}</MemberEmail>}
                        </MemberText>
                        {
                            isAdmin ? 
                            (
                                <Tooltip 
                                    title={translate("web:settings.admin")}
                                    placement='top'>
                                    <AdminPanelSettingsIcon style={{color: 'green'}} /> 
                                </Tooltip>
                            )
                            : isModerator && 
                            ( 
                                <Tooltip
                                    placement='top'
                                    title={translate("web:settings.moderator")}
                                    >
                                    <SupervisorAccountIcon style={{color: '#73ac73'}}/> 
                                </Tooltip>
                            )
                        }    
                    </MemberNameWrap>
                </MemberInfoBlock>


                <MemberActions ref={ref1}>
                    <DotsTrigger onClick={handleDropDown} type="button"><MoreHorizIcon /></DotsTrigger>
                    { isDropped && (
                            <MemberMenu>
                                <MemberMenuItem onClick={handleRemove} > 
                                    <PersonRemoveIcon />  
                                    <span style={{marginLeft: '11px'}}>
                                        {translate("web:settings.removeUser")}
                                    </span> </MemberMenuItem>
                                {
                                    isAdmin ?
                                    (
                                        <MemberMenuItem onClick={handleRemoveAdmin} > 
                                            <AdminPanelSettingsIcon />  
                                                <span>
                                                    {
                                                        translate("web:settings.removeUserAdmin")
                                                    }
                                                </span> 
                                            </MemberMenuItem>
                                    )
                                    : isModerator ?
                                    (
                                        <MemberMenuItem onClick={handleRemoveModerator}> 
                                            <SupervisorAccountIcon />  
                                            <span>
                                                {
                                                    translate("web:settings.removeUserModerator")
                                                }
                                            </span> 
                                        </MemberMenuItem>
                                    )
                                    : !isAdmin && !isModerator && (
                                        <>
                                            <MemberMenuItem onClick={handleMakeAdmin}> 
                                                <AdminPanelSettingsIcon />  
                                                <span>
                                                    {
                                                        translate("web:settings.makeUserAdmin")
                                                    }
                                                </span> 
                                            </MemberMenuItem>
                                            <MemberMenuItem onClick={handleMakeModerator}> 
                                                <SupervisorAccountIcon />  
                                                <span>
                                                    {
                                                        translate("web:settings.makeUserModerator")
                                                    }
                                                </span> 
                                            </MemberMenuItem>  
                                            <MemberMenuItem onClick={loadMemberRoles}>
                                                <BadgeIcon />
                                                 <span>{translate('settings:membership.manageRoles')}</span>
                                            </MemberMenuItem>
                                        </>                                  
                                    )

                                }
                                {(isAdmin || isModerator) && (
                                    <MemberMenuItem onClick={loadMemberRoles}>
                                        <BadgeIcon />
                                         <span>{translate('settings:membership.manageRoles')}</span>
                                    </MemberMenuItem>
                                )}

                            </MemberMenu>
                        )
                    }
                </MemberActions> 
                <Modal
                    open={rolesOpen}
                    onClose={() => setRolesOpen(false)}
                    style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                >
                    <RoleModal>
                        <RoleModalHeader>
                            <div>
                                <RoleModalTitle>{fullName}</RoleModalTitle>
                                 <RoleModalSub>{translate('settings:membership.rolesHelp')}</RoleModalSub>
                            </div>
                            <CloseButton onClick={() => setRolesOpen(false)}><CloseIcon /></CloseButton>
                        </RoleModalHeader>

                        {rolesLoading ? (
                            <RoleLoading><SpinnerSmall /></RoleLoading>
                        ) : roles.length === 0 ? (
                             <RoleEmpty>{translate('settings:membership.createRolesHelp')}</RoleEmpty>
                        ) : (
                            <RoleChipWrap>
                                {roles.map((role) => {
                                    const selected = selectedRoleIds.includes(String(role.id))
                                    return (
                                        <RoleChip
                                            key={role.id}
                                            selected={selected}
                                            onClick={() => toggleRole(String(role.id))}
                                        >
                                            <span>{role.name}</span>
                                            {role.description && <RoleChipDescription>{role.description}</RoleChipDescription>}
                                        </RoleChip>
                                    )
                                })}
                            </RoleChipWrap>
                        )}

                        <SaveRolesButton onClick={saveMemberRoles} disabled={rolesSaving || rolesLoading || roles.length === 0}>
                            {rolesSaving ? <SpinnerSmall /> : 'Save Roles'}
                        </SaveRolesButton>
                    </RoleModal>
                </Modal>
        </MemberRow>
    )
}

export default MyMembersItem

const MemberMenuItem = styled.li`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    white-space: nowrap;
    padding: 5px 10px;
    color: rgb(103, 103, 103);
    font-size: 14px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    border-radius: 4px;
    span{
        margin-left: 10px;
    }
    &:hover{
        background-color: rgb(228, 228, 228);
    }
`

const MemberRow = styled.div`
    width: 100%;
    min-width: 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 12px;
    border: 1px solid #edf0f2;
    border-radius: 10px;
    background: #fff;
    box-sizing: border-box;

    &:hover {
        border-color: #d8e0e8;
        background: #fbfcfd;
    }
`

const MemberInfoBlock = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
`

const MemberAvatar = styled.img`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    background: #f1f3f5;
`

const MemberText = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`

const MemberName = styled.div`
    max-width: 260px;
    font-size: 14px;
    font-weight: 700;
    color: var(--lightThemeFontColor);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`

const MemberEmail = styled.div`
    max-width: 260px;
    margin-top: 2px;
    font-size: 12px;
    color: #777;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`

const MemberActions = styled.div`
    position: relative;
    flex-shrink: 0;
`

const DotsTrigger = styled.button`
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid transparent;
    border-radius: 8px;
    background: transparent;
    color: var(--lightThemeFontColor);
    cursor: pointer;

    &:hover {
        background: rgba(0, 180, 204, 0.08);
        border-color: rgba(0, 180, 204, 0.18);
        color: var(--themeColor);
    }
`

const MemberMenu = styled.ul`
    position: absolute;
    top: 38px;
    right: 0;
    z-index: 4;
    min-width: 220px;
    max-width: min(320px, 80vw);
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 6px;
    margin: 0;
    list-style: none;
    background: #fff;
    border: 1px solid #d8e0e8;
    border-radius: 10px;
    box-shadow: 0 12px 26px rgba(15, 23, 42, 0.16);
`

const RoleModal = styled.div`
    width: 480px;
    max-width: 92vw;
    background: #fff;
    border-radius: 14px;
    padding: 16px;
    box-sizing: border-box;
`
const RoleModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 14px;
`
const RoleModalTitle = styled.div`
    font-size: 18px;
    font-weight: 800;
    color: var(--lightThemeFontColor);
`
const RoleModalSub = styled.div`
    font-size: 12px;
    color: #777;
    margin-top: 3px;
    line-height: 1.35;
`
const CloseButton = styled.button`
    border: none;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    cursor: pointer;
`
const RoleLoading = styled.div`
    padding: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
`
const RoleEmpty = styled.div`
    color: #777;
    font-size: 13px;
    padding: 16px 0;
`
const RoleChipWrap = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    margin: 10px 0 16px;
`
const RoleChip = styled.button`
    width: 100%;
    border: 1px solid ${props => props.selected ? 'var(--themeColor)' : '#ddd'};
    background: ${props => props.selected ? 'rgba(0, 180, 204, 0.1)' : '#f7f7f7'};
    color: var(--lightThemeFontColor);
    border-radius: 9px;
    padding: 10px 12px;
    font-weight: 700;
    cursor: pointer;
    text-align: left;
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    &:hover {
        border-color: var(--themeColor);
    }
`
const RoleChipDescription = styled.span`
    margin-top: 3px;
    color: #777;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.35;
`
const SaveRolesButton = styled.button`
    width: 100%;
    border: none;
    border-radius: 9px;
    padding: 11px;
    color: #fff;
    background: var(--themeColor);
    font-weight: 800;
    cursor: pointer;
    &:disabled { opacity: .55; cursor: not-allowed; }
`
